import { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { RequestHelper } from "../../../utils/requestHelper";
import { _ROUTES } from "../../../utils/_routes_constants";
import { getDataGeneric } from "../../../repository/DataGeneric";
import {
  _ACTIONS,
  _HTTP,
  _MESSAGES,
  _SWAL_ICONS,
} from "../../../utils/constants";
import { createToast } from "../../../utils/toast";
import { buildIRI } from "../../../utils/function";
import {
  defaultColDef,
  excelStylesPayroll,
  formDataToRow,
  PAYROLL_ROWS,
  recalcAggregatorRows,
} from "./utils";
import { Box } from "@mui/material";
import ExcellExport from "./ExcellExport";
import ToggleFullScreen from "./ToggleFullScreen";
import { isNull } from "lodash";
import { SaveDataButton } from "../SaveDataButton";
import {
  enhanceColumnsWithParser,
  parseInternationalNumber,
} from "../BaseBudgetSaveFunctions";

const TablePayroll = ({
  rows,
  columns,
  setRows,
  params,
  saveData,
  setSaveData,
}) => {
  const requestHelper = new RequestHelper();
  const [payrollByMonth, setPayrollByMonth] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    if (!params?.site || !params?.activity) {
      return;
    }
    const queryParams = {
      site: params.site,
      activity: params.activity,
    };

    if (params.fiscalYear) queryParams.fiscalYear = params.fiscalYear;
    if (params.version) queryParams.version = params.version;

    getDataGeneric(
      requestHelper,
      queryParams,
      (data) => {
        const { rows: rowData, payrollMonthMap } = formDataToRow(
          data,
          params.fiscalYear,
        );
        setRows(rowData);
        setPayrollByMonth(payrollMonthMap);
      },
      _ROUTES.IPT_PAYROLLS,
      "Unable to retrieve payroll rows",
    );
  }, [params.site, params.activity]);

  /**
   * Save Cell value changed
   */
  const onCellValueChanged = useCallback(
    async (agParams) => {
      const { colDef, data, newValue } = agParams;
      const field = colDef.field;
      const month = parseInt(field, 10);
      // get the row field
      const rowField = PAYROLL_ROWS.find((f) => f.rowName === data.rowName);
      if (!rowField) return;
      // get the field key ('basic', 'overtime')
      const fieldKey = rowField.key;

      const existing = payrollByMonth[month] || {};

      const numericValue = parseInternationalNumber(newValue) || 0;

      let payload = {
        ...existing,
        [fieldKey]: numericValue,
        fiscalMonth: month,
        fiscalYear: params.fiscalYear,
        site: buildIRI("/sites", params.site),
        activity: buildIRI("/activities", params.activity),
        version: params.version || 1,
      };

      if (!existing.id) {
        PAYROLL_ROWS.forEach(({ key }) => {
          if (payload[key] === undefined) {
            payload[key] = 0;
          }
        });
      }

      const methode = existing.id ? _ACTIONS.PATCH : _ACTIONS.POST;
      const url = existing.id
        ? `${_ROUTES.IPT_PAYROLLS}/${existing.id}`
        : _ROUTES.IPT_PAYROLLS;

      requestHelper
        .doRequest(methode, url, payload)
        .then((res) => {
          if (res.status === _HTTP.created || res.status === _HTTP.ok) {
            createToast(_SWAL_ICONS.success, "Payroll saved successfully");

            const saved = res.data;
            const newMonthMap = {
              ...payrollByMonth,
              [month]: saved,
            };
            setPayrollByMonth(newMonthMap);

            setRows((prevRows) => {
              const newRows = prevRows.map((row) => {
                if (row.rowName === data.rowName) {
                  return { ...row, [field]: numericValue };
                }

                return row;
              });

              return recalcAggregatorRows(newRows);
            });
          }
        })
        .catch(() => {
          createToast(_SWAL_ICONS.error, _MESSAGES.error);
        });
    },
    [payrollByMonth, params, requestHelper, setRows, setPayrollByMonth],
  );

  // Améliorer les colonnes avec le parser
  const enhancedColumns = enhanceColumnsWithParser(columns);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
        <ToggleFullScreen
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
        />

        <ExcellExport gridRef={gridRef} />
      </Box>
      <Box id={"grid-container"} style={{ height: 450, width: "100%" }}>
        <AgGridReact
          ref={gridRef}
          rowData={rows}
          columnDefs={enhancedColumns}
          cellSelection={true}
          copyHeadersToClipboard={false}
          defaultColDef={defaultColDef}
          excelStyles={excelStylesPayroll}
          onCellValueChanged={onCellValueChanged}
          rowHeight={30}
          suppressClipboardPaste={false}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          getRowClass={(params) => {
            if (params.data && params.data.isAggregator) {
              return "total-row";
            }

            return null;
          }}
        />
        {!isNull(saveData) && (
          <SaveDataButton gridRef={gridRef} setSaveData={setSaveData} />
        )}
      </Box>
    </>
  );
};

export default TablePayroll;
